import { useQuery } from "@tanstack/react-query";
import { bridgeStatusQueryKey, fetchBridgeStatus } from "@common/lib/api/bridge";
import { useAccountContext } from "@providers/AccountProvider";
import Spinner from "@/common/components/Spinner";
import { timeAgo } from "@/common/utils/helpers";
import BridgeTroubleshooting from "./BridgeTroubleshooting";

const STATUS_CONFIG = {
    healthy: { label: "Connected", color: "bg-green-500", textColor: "text-green-700" },
    degraded: { label: "Degraded", color: "bg-yellow-500", textColor: "text-yellow-700" },
    error: { label: "Error", color: "bg-red-500", textColor: "text-red-700" },
} as const;

export default function BridgeStatus() {
    const { accountData } = useAccountContext();
    const { data, isLoading, error } = useQuery({
        // One cache entry, four readers: this badge, `useBridgeUpgradeOffer` (which the
        // BridgeUpgradeCta card — on this page and on the Usage dashboard — and the app-wide
        // BridgeUpgradeBanner all call), the Integrations page's own gate for the block this
        // renders inside, and the install wizard's entry gate.
        //
        // `fetchBridgeStatus`, not a second inline apiFetch on the same key. React Query gives
        // every observer of a key the `queryFn` of whichever one mounted LAST, so two different
        // functions behind one key make behaviour depend on mount order — and the two differ in
        // the way that matters: `fetchBridgeStatus` throws on a failed check, where an inline
        // apiFetch can resolve to something that reads like `installed: false` and tells a clinic
        // with a working bridge to install it again.
        queryKey: bridgeStatusQueryKey(accountData?.id),
        queryFn: fetchBridgeStatus,
        staleTime: 60_000,
        refetchInterval: 5 * 60_000,
    });

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner /> Checking bridge status…
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm text-gray-500">Couldn&rsquo;t check bridge status. Refresh to try again.</p>
            </div>
        );
    }

    if (!data) return null;

    if (!data.installed) {
        return (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm text-gray-500">
                    Bridge not installed. Select your practice management system below to download.
                </p>
            </div>
        );
    }

    const health = data.latest_health;
    // `status` is nullable on the shared `BridgeHealth` type — a heartbeat row can arrive
    // before the bridge has classified itself — and an unclassified one reads as "Unknown"
    // below, the same as no heartbeat at all.
    const statusConfig = health?.status ? STATUS_CONFIG[health.status] : null;

    return (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {statusConfig && <span className={`inline-block h-2.5 w-2.5 rounded-full ${statusConfig.color}`} />}
                    <span className={`text-sm font-medium ${statusConfig?.textColor ?? "text-gray-500"}`}>
                        {statusConfig?.label ?? "Unknown"}
                    </span>
                    {data.bridge_version && <span className="text-xs text-gray-400">v{data.bridge_version}</span>}
                </div>
                {data.last_seen_at && (
                    <span className="text-xs text-gray-400">Last seen {timeAgo(data.last_seen_at)}</span>
                )}
            </div>

            {health && (health.status === "degraded" || health.status === "error") && (
                <div className="flex gap-4 text-xs text-gray-500">
                    {/* ezyVet has no local database — it's a cloud browser-automation session, not
                        a PIMS server process on this machine — so this line is never meaningful
                        for it. */}
                    {data.module_type !== "ezyvet" && (
                        <span>Database: {health.db_reachable ? "reachable" : "unreachable"}</span>
                    )}
                    <span>Cloud: {health.pubsub_reachable ? "reachable" : "unreachable"}</span>
                    {health.queue_depth > 0 && <span>Queue: {health.queue_depth}</span>}
                </div>
            )}

            {data.is_active === false && (
                <p className="text-xs text-red-600">This bridge installation has been deactivated. Contact support.</p>
            )}

            {health && (health.status === "degraded" || health.status === "error") && (
                <BridgeTroubleshooting
                    // Both are nullable on the shared type. Unreported is not confirmed, and the
                    // line above already reads it that way ("unreachable"), so the troubleshooting
                    // advice agrees with the badge rather than contradicting it.
                    dbReachable={health.db_reachable === true}
                    pubsubReachable={health.pubsub_reachable === true}
                    lastSeenAt={data.last_seen_at}
                    moduleType={data.module_type}
                />
            )}
        </div>
    );
}
